<template>
  <v-container grid-list-md>
    <pi-progress v-if="progress" main></pi-progress>
    <v-alert
      :value="!paranoid"
      type="error"
    >Poruszasz się w trybie administratora. Wszystkie pliki które teraz usuniesz zostaną trwale usunięte.</v-alert>
    <pi-navigation :navigation="navigation"></pi-navigation>

    <v-subheader v-if="files.length == 0 && folders.length == 0">Brak elementów</v-subheader>

    <v-layout row wrap v-if="folders.length > 0">
      <pi-folders
        :folders="folders"
        @showModifyFolderDialog="showFolderDialog($event)"
        @showDeleteDialog="showDeleteDialog($event, true)"
      ></pi-folders>
    </v-layout>

    <v-layout row wrap v-if="files.length > 0">
      <pi-files
        :files="files"
        :paranoidUrl="paranoidUrl"
        @showRestoreDialog="showRestoreDialog($event)"
        @showDeleteDialog="showDeleteDialog($event, false)"
        @showVersionDialog="showVersionDialog($event)"
        @showInfoDialog="showInfoDialog($event)"
      ></pi-files>
    </v-layout>

    <v-layout row wrap>
      <pi-changes :id="treeId" :key="treeKey"></pi-changes>
    </v-layout>

    <v-speed-dial v-model="fab" bottom right fixed>
      <v-btn v-model="fab" slot="activator" color="accent" fab dark>
        <v-icon>more_vert</v-icon>
        <v-icon>close</v-icon>
      </v-btn>
      <v-btn v-if="isAll || treeId != 0" fab dark small color="success" @click="showFolderDialog()">
        <v-icon>create_new_folder</v-icon>
      </v-btn>
      <v-btn v-if="treeId != 0" fab dark small color="info" @click="addFileDialog = true">
        <v-icon>cloud_upload</v-icon>
      </v-btn>
      <v-btn
        v-if="Auth.check(Permissions.REPOSITORY_PARANOID)"
        fab
        dark
        small
        :color="paranoid ? 'error' : 'success'"
        @click="paranoid = !paranoid"
      >
        <v-icon>security</v-icon>
      </v-btn>
    </v-speed-dial>

    <pi-dialog
      :dialog="folderDialog.on"
      :progress="folderDialog.progress"
      :headline="folderDialog.add ? 'Tworzenie folderu' : `Edycja folderu ${folderDialog.obj.name}`"
      :operation="folderDialog.add ? 'Dodaj' : 'Edytuj'"
      @closed="showFolderDialog(false)"
      @action="folderDialog.add ? addFolder() : modifyFolder()"
    >
      <v-form ref="folderForm" @submit.prevent lazy-validation>
        <v-text-field
          v-model="folderDialog.obj.name"
          :rules="Rules.fileTree.name"
          :disabled="folderDialog.progress"
          required
          name="name"
          label="Nazwa"
        ></v-text-field>
      </v-form>
    </pi-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title
          v-if="this.isDeletingFolder"
          class="headline"
        >Usuwanie folderu {{this.deleteObject.name}}</v-card-title>
        <v-card-title
          v-if="!this.isDeletingFolder"
          class="headline"
        >Usuwanie pliku {{this.deleteObject.name}}</v-card-title>
        <v-card-text>Czy chcesz usunąć {{ deleteObject.name }}?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="accent" flat @click="deleteDialog = false">Odrzuć</v-btn>
          <v-btn color="error" flat @click="deleteItem()">Usuń</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="restoreDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">Przywracanie pliku {{this.restoreObject.name}}</v-card-title>
        <v-card-text>Czy chcesz przywrócić {{ restoreObject.name }} i wszystkie jego wersje?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="accent" flat @click="restoreDialog = false">Odrzuć</v-btn>
          <v-btn color="info" flat @click="restoreItem()">Przywróć</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <pi-dialog
      :dialog="infoDialog.on"
      :headline="`Informacje o ${this.infoDialog.obj.name}`"
      hideOperation="true"
      @closed="showInfoDialog(false)"
    >
      <v-list>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              Nazwa:
              <strong>{{ this.infoDialog.obj.name }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              Typ:
              <strong>{{ this.infoDialog.obj.type }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              Rozmiar:
              <strong>{{ this.infoDialog.obj.size | humanSize }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              Liczba wersji:
              <strong>{{ this.infoDialog.obj.version }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              Zmiana:
              <strong>{{ this.infoDialog.obj.FileTreeChange ? this.infoDialog.obj.FileTreeChange.text : '' }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              Data zmiany:
              <strong>{{ this.infoDialog.obj.FileTreeChange ? this.infoDialog.obj.FileTreeChange.createdAt : 0 | humanDate }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              Autor zmiany:
              <strong>{{ this.infoDialog.obj.FileTreeChange ? `${this.infoDialog.obj.FileTreeChange.User.name} ${this.infoDialog.obj.FileTreeChange.User.surname}` : '' }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
    </pi-dialog>

    <v-dialog v-model="versionDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">Wersje pliku {{this.versionObject.name}}</v-card-title>
        <v-card-text>
          <pi-files
            :files="versions"
            :paranoidUrl="paranoidUrl"
            specific="true"
            @showRestoreDialog="showRestoreDialog($event)"
            @showDeleteDialog="showDeleteDialog($event, false)"
            @showVersionDialog="showVersionDialog($event)"
          ></pi-files>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="accent" flat @click="versionDialog = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <pi-addfiledialog
      :addFileDialog="addFileDialog"
      :id="treeId"
      @added="filesAdded"
      @closed="addFileDialog = false"
    ></pi-addfiledialog>
    <pi-snackbar :color="snackbar.color" :snackbar.sync="snackbar.on" :message="snackbar.message"></pi-snackbar>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    progress: false,
    snackbar: { on: false, color: "error", message: "" },
    fab: false,
    folders: [],
    files: [],
    navigation: [],
    navigationItems: [],
    folderDialog: { on: false, obj: {}, progress: false, add: false },
    addFolderDialog: false,
    isModifyFolder: false,
    addFolderData: { name: "Folder" },
    deleteDialog: false,
    isDeletingFolder: true,
    deleteObject: { name: "" },
    addFileDialog: false,
    restoreDialog: false,
    restoreObject: { name: "" },
    treeKey: 0,
    versionDialog: false,
    versionObject: { name: "" },
    versions: [],
    infoDialog: { on: false, obj: {} },
    afterMount: false,
    paranoid: true
  }),
  computed: {
    treeId() {
      return this.$route.params.treeId ? this.$route.params.treeId : 0;
    },
    paranoidUrl() {
      return this.paranoid ? "" : "/force";
    },
    basicUrl() {
      return this.isAll
        ? `/filetrees${this.paranoidUrl}`
        : `/filetrees/${this.treeId}${this.paranoidUrl}`;
    }
  },
  props: { isAll: Boolean },
  methods: {
    async getData() {
      this.progress = true;
      const response = await this.Api.send("get", this.basicUrl);
      this.progress = false;
      if (response.status) {
        this.folders = response.fileTrees;
        this.files = response.files;
        this.navigationItems = response.navigation;
        return;
      }
      this.showError(response.message);
    },
    async createNavigation() {
      let i = 0;
      this.navigation = [
        { id: i++, text: "Repozytorium", href: "/filetree" },
        ...this.navigationItems.map(n => {
          return { id: i++, text: n.name, href: `/filetree/${n.id}` };
        })
      ];
      this.navigation[this.navigation.length - 1].disabled = true;
    },
    async filesAdded(v) {
      if (!v) return;
      this.addFileDialog = false;
      this.getData();
      this.treeKey++;
    },
    async showInfoDialog(obj) {
      this.infoDialog.obj = obj ? obj : {};
      this.infoDialog.on = obj !== false;
    },
    async showFolderDialog(obj) {
      this.folderDialog.obj = obj ? obj : { name: "", parentId: this.treeId };
      this.folderDialog.add = !obj;
      this.folderDialog.on = obj !== false;
    },
    async addFolder() {
      if (!this.$refs.folderForm.validate()) {
        this.showError("Uzupełnij wymagane pola");
        return;
      }
      this.folderDialog.progress = true;
      const response = await this.Api.send("post", "/filetrees", {
        fileTreeData: this.folderDialog.obj
      });
      this.folderDialog.progress = false;
      if (response.status) {
        this.getData();
        this.showSuccess("Folder dodany");
        this.showFolderDialog(false);
        return;
      }
      this.showError(response.message);
    },
    async modifyFolder() {
      if (!this.$refs.folderForm.validate()) {
        this.showError("Uzupełnij wymagane pola");
        return;
      }
      this.folderDialog.progress = true;
      const response = await this.Api.send(
        "put",
        `/filetrees/${this.folderDialog.obj.id}`,
        {
          fileTreeData: this.folderDialog.obj
        }
      );
      this.folderDialog.progress = false;
      if (response.status) {
        this.getData();
        this.showSuccess("Folder zmodyfikowany");
        this.showFolderDialog(false);
        return;
      }
      this.showError(response.message);
    },
    async showDeleteDialog(obj, isFolder) {
      this.deleteObject = obj;
      this.isDeletingFolder = isFolder;
      this.deleteDialog = true;
    },
    async deleteItem() {
      this.progress = true;
      const url = this.isDeletingFolder ? "/filetrees" : "/files";
      const paranoidUrl = this.isDeletingFolder ? "" : this.paranoidUrl;
      const versionUrl = this.versionDialog
        ? `/${this.deleteObject.version}`
        : "";
      const response = await this.Api.send(
        "delete",
        `${url}/${this.deleteObject.id}${versionUrl}${paranoidUrl}`
      );
      this.progress = false;
      if (response.status) {
        if (this.versionDialog) this.getVersions(this.versionObject);
        this.getData();
        this.showSuccess(
          this.isDeletingFolder ? "Folder usunięty" : "Plik usunięty"
        );
        this.deleteDialog = false;
        return;
      }
      this.showError(response.message);
    },
    async showRestoreDialog(obj) {
      this.restoreObject = obj;
      this.restoreDialog = true;
    },
    async restoreItem() {
      this.progress = true;
      const versionUrl = !this.paranoid ? `/${this.restoreObject.version}` : "";
      const response = await this.Api.send(
        "put",
        `/files/${this.restoreObject.id}${versionUrl}/restore`
      );
      this.progress = false;
      if (response.status) {
        if (this.versionDialog) this.getVersions(this.versionObject);
        this.getData();
        this.showSuccess("Plik przywrócony");
        this.restoreDialog = false;
        return;
      }
      this.showError(response.message);
    },
    async showVersionDialog(obj) {
      this.versionObject = obj;
      this.versionDialog = true;
      this.getVersions(obj);
    },
    async getVersions(obj) {
      this.progress = true;
      const response = await this.Api.send(
        "get",
        `/files/${obj.id}/versions${this.paranoidUrl}`
      );
      this.progress = false;
      if (response.status) {
        this.versions = response.files;
        return;
      }
      this.showError(response.message);
    },
    async showError(message) {
      this.snackbar.color = "error";
      this.showMessage(message);
    },
    async showSuccess(message) {
      this.snackbar.color = "success";
      this.showMessage(message);
    },
    async showMessage(message) {
      this.snackbar.message = message;
      this.snackbar.on = true;
    }
  },
  async mounted() {
    if (localStorage.paranoid) {
      this.paranoid =
        localStorage.paranoid == "true" || localStorage.paranoid == true;
    }
    await this.getData();
    this.createNavigation();
    this.afterMount = true;
  },
  watch: {
    paranoid(s) {
      localStorage.paranoid = s;
      if (this.afterMount) this.getData();
    }
  }
};
</script>